import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { fetchAlbumWithSongs, fetchAllData } from '../lib/api';
import type { Song, Artist, Album } from '../types/models';
import { usePlayer } from '../lib/PlayerContext';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import { Link } from 'react-router-dom';
import { SongCard } from '../components/SongCard';
import { getArtistNames } from '../utils/artistUtils';

export function Album() {
  const { id } = useParams<{ id: string }>();
  const [album, setAlbum] = useState<Album | null>(null);
  const [songs, setSongs] = useState<Song[]>([]);
  const [artist, setArtist] = useState<Artist | null>(null);
  const [allArtists, setAllArtists] = useState<Artist[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const player = usePlayer();

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        setError(null);
        
        if (id) {
          console.log('Loading album with ID:', id);
          
          // Fetch the specific album with its songs from the API
          const albumData = await fetchAlbumWithSongs(id);
          console.log('Album data received:', albumData);
          
          if (albumData.album) {
            setAlbum(albumData.album);
            setSongs(albumData.songs || []);
            
            // Get all app data to find the artist
            const appData = await fetchAllData();
            setAllArtists(appData.artists);
            const foundArtist = appData.artists.find(a => a.id === albumData.album.artistId);
            if (foundArtist) {
              setArtist(foundArtist);
            } else {
              console.warn('Artist not found for ID:', albumData.album.artistId);
            }
          } else {
            setError('Album data is missing from response');
          }
        } else {
          setError('No album ID provided');
        }
      } catch (error) {
        console.error('Error loading album data:', error);
        setError(error instanceof Error ? error.message : 'Failed to load album');
      } finally {
        setLoading(false);
      }
    }
    
    loadData();
  }, [id]);

  const playAlbum = () => {
    if (songs.length > 0) {
      // Play the first song and queue the rest
      player.playSong(songs[0]);
      // In a complete implementation, we would also add the rest to queue
    }
  };

  const shufflePlay = () => {
    if (songs.length > 0) {
      // Shuffle the songs and play
      const shuffledSongs = [...songs].sort(() => Math.random() - 0.5);
      player.playSong(shuffledSongs[0]);
      // In a complete implementation, we would also add the rest to queue
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-naia-pink text-xl">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center flex-col">
        <div className="text-error text-xl mb-2">Error loading album</div>
        <div className="text-text-muted">{error}</div>
        <div className="text-text-subtle text-sm mt-2">Album ID: {id}</div>
      </div>
    );
  }

  if (!album || !artist) {
    return (
      <div className="min-h-screen flex items-center justify-center flex-col">
        <div className="text-slate-grey text-xl mb-2">Album not found</div>
        <div className="text-text-muted text-sm">Album ID: {id}</div>
        <div className="text-text-muted text-sm mt-1">
          Album: {album ? 'Found' : 'Not found'} | Artist: {artist ? 'Found' : 'Not found'}
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-32 px-6 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row gap-8 items-start">
        {/* Album Cover */}
        <div className="w-full md:w-1/3 lg:w-1/4">
          <div className="relative aspect-square rounded-xl overflow-hidden shadow-2xl shadow-naia-pink/10 ring-1 ring-white/10">
            <img 
              src={album.coverUrl} 
              alt={album.title}
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Album Info */}
        <div className="flex-1">
          <h1 className="text-3xl md:text-4xl font-bold text-pearl-white mb-1">{album.title}</h1>
          
          <Link 
            to={`/artist/${artist.id}`}
            className="flex items-center gap-2 mt-2 mb-1"
          >
            <img 
              src={artist.imageUrl} 
              alt={artist.name}
              className="w-6 h-6 rounded-full object-cover"
            />
            <span className="text-slate-grey hover:text-pearl-white transition-colors">
              {artist.name}
            </span>
          </Link>
          
          <div className="text-slate-grey mt-1 mb-6">
            {songs.length} {songs.length === 1 ? 'song' : 'songs'} • Released {album.releaseDate}
          </div>

          <div className="flex flex-wrap gap-3 mt-4">
            <button 
              onClick={playAlbum}
              className="px-6 py-3 bg-naia-pink hover:bg-naia-pink/90 rounded-full text-pearl-white font-medium flex items-center gap-2 transition-all duration-300 shadow-lg shadow-naia-pink/20"
            >
              <PlayArrowIcon />
              Play All
            </button>

            <button 
              onClick={shufflePlay}
              className="px-6 py-3 bg-white/5 hover:bg-white/10 rounded-full text-slate-grey hover:text-pearl-white font-medium flex items-center gap-2 transition-all duration-300"
            >
              <ShuffleIcon />
              Shuffle Play
            </button>
          </div>
        </div>
      </div>

      {/* Song List */}
      <div className="mt-12">
        <h2 className="text-xl font-medium text-pearl-white mb-4">Songs</h2>
        <div className="flex flex-col gap-2">
          {songs.map((song, index) => (
            <div key={song.id} className="flex items-center gap-4 p-4 hover:bg-white/5 rounded-lg transition-all duration-300">
              <div className="text-slate-grey w-6 text-center">{index + 1}</div>
              <SongCard 
                song={song} 
                artist={getArtistNames(song.artistId, allArtists)} 
                voicebank={song.synthId ? 'Various' : ''} 
                layout="list" 
              />
            </div>
          ))}
        </div>

        {/* Copyright and Record Label Info */}
        {(album.copyright || album.recordLabel) && (
          <div className="mt-8 text-sm text-slate-grey border-t border-white/10 pt-8">
            {album.copyright && (
              <div className="mb-1">
                <span className="font-medium">Copyright:</span> {album.copyright}
              </div>
            )}
            {album.recordLabel && (
              <div>
                <span className="font-medium">Record Label:</span> {album.recordLabel}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
